import React, { useState, useEffect, useRef, useContext } from "react";
import { useFormContext } from "react-hook-form";
import VisitorParamsContext from "../../../../Contexts/VisitorParams";

const UkCreditRating = ({ show, onStatusChange }) => {
  const [loader, setLoader] = useState(true);
  const [iframeUrl, setIframeUrl] = useState("");
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [previousFormData, setPreviousFormData] = useState({});
  const [stableUuid, setStableUuid] = useState("");
  const iframeRef = useRef(null);
  const { getValues } = useFormContext();
  const { visitorParameters } = useContext(VisitorParamsContext);

  const baseUrl = "https://uk.rspcdn.com/xprr/red/PID/4421";

  // Generate stable UUID on initial load
  useEffect(() => {
    if (!stableUuid) {
      const uuid = visitorParameters?.visitor_parameters?.uuid || `TMUC099-${Date.now()}`;
      setStableUuid(uuid);
    }
  }, [visitorParameters, stableUuid]);

  // Add onBlur event listeners to relevant fields
  useEffect(() => {
    if (hasInitiallyLoaded && show) {
      const relevantFields = [
        'lstSalutation', 'txtFName', 'txtLName', 'txtEmail', 'txtPhone',
        'txtHouseNumber', 'txtAddress2', 'address1', 'txtStreet', 'txtTown', 'txtPostCode'
      ];

      const handleFieldBlur = () => {
        const currentFormData = getValues();
        
        // Add current DOB values to form data for comparison
        currentFormData.dobDay = document.getElementById("DobDay")?.value || '';
        currentFormData.dobMonth = document.getElementById("DobMonth")?.value || '';
        currentFormData.dobYear = document.getElementById("DobYear")?.value || '';
        
        if (hasFormDataChanged(currentFormData, previousFormData)) {
          console.log("Field blur detected with data change, reloading iframe");
          const newUrl = buildIframeUrl();
          setIframeUrl(newUrl);
          setPreviousFormData(currentFormData);
          setLoader(true);
          
          const timer = setTimeout(() => {
            setLoader(false);
          }, 1500);
          
          return () => clearTimeout(timer);
        }
      };

      // Add blur listeners to all relevant fields
      relevantFields.forEach(fieldId => {
        const element = document.getElementById(fieldId);
        if (element) {
          element.addEventListener('blur', handleFieldBlur);
        }
      });

      // Also add blur and change listeners to DOB fields (dropdowns need change events)
      ['DobDay', 'DobMonth', 'DobYear'].forEach(fieldId => {
        const element = document.getElementById(fieldId);
        if (element) {
          element.addEventListener('blur', handleFieldBlur);
          element.addEventListener('change', handleFieldBlur); // For dropdown/select elements
        }
      });

      return () => {
        // Clean up event listeners
        relevantFields.forEach(fieldId => {
          const element = document.getElementById(fieldId);
          if (element) {
            element.removeEventListener('blur', handleFieldBlur);
          }
        });
        ['DobDay', 'DobMonth', 'DobYear'].forEach(fieldId => {
          const element = document.getElementById(fieldId);
          if (element) {
            element.removeEventListener('blur', handleFieldBlur);
            element.removeEventListener('change', handleFieldBlur);
          }
        });
      };
    }
  }, [hasInitiallyLoaded, show, previousFormData]);

  useEffect(() => {
    if (show) {
      const url = buildIframeUrl();
      console.log("UKC Iframe URL:", url);
      setIframeUrl(url);
      setHasInitiallyLoaded(true);
      
      // Store initial form data including DOB values
      const initialFormData = getValues();
      initialFormData.dobDay = document.getElementById("DobDay")?.value || '';
      initialFormData.dobMonth = document.getElementById("DobMonth")?.value || '';
      initialFormData.dobYear = document.getElementById("DobYear")?.value || '';
      setPreviousFormData(initialFormData);

      const timer = setTimeout(() => {
        setLoader(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [show]);

  
  // Function to check if relevant form data has changed
  const hasFormDataChanged = (current, previous) => {
    const relevantFields = [
      'lstSalutation', 'txtFName', 'txtLName', 'txtEmail', 'txtPhone',
      'txtHouseNumber', 'txtAddress2', 'address1', 'txtStreet', 'txtTown', 'txtPostCode'
    ];
    
    // Check personal data changes
    const hasPersonalDataChanged = relevantFields.some(field => current[field] !== previous[field]);
    
    // Check DOB changes - compare current DOM values with stored previous values
    const currentDobDay = document.getElementById("DobDay")?.value;
    const currentDobMonth = document.getElementById("DobMonth")?.value;
    const currentDobYear = document.getElementById("DobYear")?.value;
    
    const prevDobDay = previous.dobDay || '';
    const prevDobMonth = previous.dobMonth || '';
    const prevDobYear = previous.dobYear || '';
    
    const hasDobChanged = currentDobDay !== prevDobDay || 
                        currentDobMonth !== prevDobMonth || 
                        currentDobYear !== prevDobYear;
    
    console.log('DOB Change Check:', {
      current: { day: currentDobDay, month: currentDobMonth, year: currentDobYear },
      previous: { day: prevDobDay, month: prevDobMonth, year: prevDobYear },
      hasDobChanged
    });
    
    return hasPersonalDataChanged || hasDobChanged;
  };

  // Listen for UKC flow completion using their official library
  // Docs: https://ratespecial.atlassian.net/wiki/spaces/UKCPUB/pages/2356543494
  useEffect(() => {
    import(
      /* webpackIgnore: true */
      "https://checkout.ukcreditratings.com/js/inform-parent.js"
    )
      .then(({ listenForUkcMessages }) => {
        listenForUkcMessages((eventData) => {
          console.log("UKC Message Received:", JSON.stringify(eventData));

          const { status, ukcGuid } = eventData;
          console.log(`UKC Flow - User ${ukcGuid} reached status: ${status}`);

          // Notify parent component (Sign.js) about the status change
          if (onStatusChange) {
            onStatusChange(status);
          }
        });
      })
      .catch((err) => {
        console.warn("Failed to load UKC inform-parent.js:", err);
      });
  }, [onStatusChange]);

  // Dynamic height using @newswire/frames
  useEffect(() => {
    if (!loader && iframeRef.current) {
      import(
        /* webpackIgnore: true */
        "https://unpkg.com/@newswire/frames/dist/frames.modern.js"
      )
        .then(({ observeIframe }) => {
          observeIframe(iframeRef.current);
        })
        .catch((err) => {
          console.warn("Failed to load @newswire/frames:", err);
        });
    }
  }, [loader]);

  const buildIframeUrl = () => {
    const formData = getValues();
    const params = new URLSearchParams();

    // Personal details
    if (formData.lstSalutation) params.append("title", formData.lstSalutation);
    if (formData.txtFName) params.append("fname", formData.txtFName);
    if (formData.txtLName) params.append("lname", formData.txtLName);
    if (formData.txtEmail) params.append("email", formData.txtEmail);
    if (formData.txtPhone) params.append("phone", formData.txtPhone);

    // Date of birth in dd/mm/yyyy format
    const dobDay = document.getElementById("DobDay")?.value;
    const dobMonth = document.getElementById("DobMonth")?.value;
    const dobYear = document.getElementById("DobYear")?.value;
    if (dobDay && dobMonth && dobYear) {
      let monthNumber = dobMonth;
      if (isNaN(dobMonth)) {
        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December",
        ];
        monthNumber = (monthNames.indexOf(dobMonth) + 1)
          .toString()
          .padStart(2, "0");
      } else {
        monthNumber = dobMonth.padStart(2, "0");
      }
      const dob = `${dobDay.padStart(2, "0")}/${monthNumber}/${dobYear}`;
      params.append("dob", dob);
    }

    // Address details
    if (formData.txtHouseNumber) {
      const houseNumber = formData.txtHouseNumber.substring(0, 12);
      params.append("abodenumber", houseNumber);
    }
    if (formData.txtAddress2) params.append("building_name", formData.txtAddress2);
    if (formData.address1) params.append("straddr", formData.address1);
    if (formData.txtStreet) params.append("street", formData.txtStreet);
    if (formData.txtTown) params.append("city", formData.txtTown);
    if (formData.txtPostCode) params.append("zip", formData.txtPostCode);

    // Special test parameters for specific emails
    if (formData.txtEmail && formData.txtEmail.toLowerCase().endsWith("@922.com")) {
      params.append("agency-test", "internal");
      params.append("agency-mock", "k");
    }

    // Client reference ID using stable UUID — also used as SID in the URL path
    const sid = `TMUC099-${stableUuid}`;
    params.append("clientRefId", sid);

    return `${baseUrl}/SID/${sid}?${params.toString()}`;
  };

  if (!show) return null;

  return (
    <div className="uk-credit-rating-section py-2 pt-0">
      {loader ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "15px 0" }}>
          <img src="assets/img/review.gif" alt="Loading..." style={{ height: "40px" }} />
        </div>
      ) : (
        <div style={{ marginTop: "20px" }}>
          <iframe
            ref={iframeRef}
            id="ukc-iframe"
            src={iframeUrl}
            frameBorder="0"
            width="100%"
            scrolling="no"
            style={{ border: "none", display: "block", width: "100%" }}
            title="UK Credit Rating"
          />
        </div>
      )}
    </div>
  );
};

export default UkCreditRating;
